import React, { useState, useEffect, useRef, createRef } from "react";
import axios from "axios";
// Importing icons for send, bot, user, ticket, and the success checkmark
import {
  SendFill,
  Robot,
  PersonCircle,
  TicketFill,
  CheckCircleFill,
  ChatDotsFill, // Added CheckCircleFill for success message
} from "react-bootstrap-icons";
import styled from "@emotion/styled";
import ChatbotTicketModal from "./ChatbotTicketModal";
// import robotWave from "../../assets/images/liveChatbot.gif";
import { oracleConfig } from "../../Utils/constant";
import urls from "../../Utils/urls";
import { useSelector } from "react-redux";

/**
 * Build a NICE-safe transcript (plain text only)
 */
const buildTranscript = (messages) =>
  messages
    .map(
      (m) =>
        `${m?.role?.toUpperCase()}: ${typeof m.text === "string" ? m.text : ""}`,
    )
    .join("\n");

/**
 * Escalate to NICE live agent with full context
 */
const handleLiveAgentEscalation = (messages, customerName, loanId) => {
  if (!window?.LoanLinQ?.openLiveAgentChat) {
    console.error("CXone not ready");
    return;
  }

  const transcript = buildTranscript(messages);
  const payload = {
    languageChoice: "en",
    transcript,
    customerName,
    loanId,
    reasonForChat: transcript,
  };
  console.log({ payload });

  window.LoanLinQ.openLiveAgentChat({
    languageChoice: "en",
    transcript,
    reasonForChat: transcript,
    customerName,
    loanId,
  });
};

/* ===================================================== */

// --- STYLES AND COLOR PALETTE (UNCHANGED) ---
const COLOR_PALETTE = {
  primary: "var(--primary-color)",
  secondary: "#F0F0F0",
  userText: "#FFFFFF",
  botText: "#333333",
  background: "#FFFFFF",
  windowBg: "#F7F9FB",
  borderColor: "#E0E0E0",
  relatedBtnBg: "#FFFFFF",
};

const styles = {
  container: {
    display: "flex",
    flexDirection: "column",
    width: "400px",
    height: "600px",
    border: `1px solid ${COLOR_PALETTE.borderColor}`,
    borderRadius: "16px",
    overflow: "hidden",
    fontFamily: "Poppins, 'Helvetica Neue', Arial, sans-serif",
    boxShadow: "0 6px 20px rgba(0, 0, 0, 0.1)",
    background: COLOR_PALETTE.background,
    position: "relative",
  },
  header: {
    display: "flex",
    alignItems: "center",
    padding: "15px",
    background: COLOR_PALETTE.primary,
    color: COLOR_PALETTE.userText,
    fontFamily: "Poppins, 'Helvetica Neue', Arial, sans-serif",
    fontWeight: "600",
    fontSize: "1.2rem",
  },
  headerIcon: {
    marginRight: "10px",
  },
  chatWindow: {
    flex: 1,
    padding: "15px",
    display: "flex",
    flexDirection: "column",
    overflowY: "auto",
    background: COLOR_PALETTE.windowBg,
  },
  messageRow: {
    display: "flex",
    marginBottom: "15px",
    maxWidth: "90%",
  },
  avatar: {
    fontSize: "1.5rem",
    marginRight: "8px",
    marginLeft: "8px",
    alignSelf: "flex-end",
    color: COLOR_PALETTE.primary,
  },
  message: {
    padding: "12px 16px",
    borderRadius: "20px",
    minWidth: "40px",
    fontSize: "0.95rem",
    lineHeight: "1.4",
  },
  userMessage: {
    marginLeft: "auto",
    background: COLOR_PALETTE.primary,
    color: COLOR_PALETTE.userText,
    borderBottomRightRadius: "4px",
    fontWeight: "400",
  },
  botMessage: {
    marginRight: "auto",
    background: COLOR_PALETTE.secondary,
    color: COLOR_PALETTE.botText,
    borderBottomLeftRadius: "4px",
  },
  // --- TICKET SUCCESS MESSAGE STYLE ---
  ticketSuccessMessage: {
    marginRight: "auto",
    background: "#D4EDDA", // Light green for success
    color: "#155724", // Dark green text
    border: "1px solid #C3E6CB",
    borderBottomLeftRadius: "4px",
    fontWeight: "500",
  },
  relatedContainer: {
    marginTop: "8px",
    marginBottom: "15px",
    alignSelf: "flex-start",
    maxWidth: "100%",
  },
  relatedTitle: {
    display: "block",
    marginBottom: "8px",
    fontSize: "0.85rem",
    color: "#666",
    fontWeight: "600",
    textTransform: "uppercase",
  },
  relatedList: {
    display: "flex",
    flexWrap: "wrap",
    gap: "8px",
  },
  relatedButton: {
    background: COLOR_PALETTE.relatedBtnBg,
    border: `1px solid ${COLOR_PALETTE.borderColor}`,
    borderRadius: "20px",
    padding: "8px 15px",
    cursor: "pointer",
    fontSize: "0.8rem",
    textAlign: "left",
    color: COLOR_PALETTE.primary,
    fontWeight: "500",
    transition: "background 0.2s",
    "&:hover": {
      background: "#E5F0FF",
    },
  },
  // --- STYLES FOR NOT FOUND SECTION ---
  notFoundContainer: {
    marginTop: "10px",
    padding: "15px",
    background: "#FEF7E6", // Light amber/yellow background for alert/suggestion
    border: "1px solid #FFD799",
    borderRadius: "12px",
    maxWidth: "100%",
  },
  actionButton: {
    marginTop: "10px",
    width: "100%",
    padding: "10px",
    background: "#D9534F", // A clear, actionable red for 'Create Ticket'
    color: "white",
    border: "none",
    borderRadius: "8px",
    fontWeight: "600",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    transition: "background 0.2s",
    "&:hover": {
      background: "#0F5BB5",
    },
  },
  suggestionList: {
    listStyleType: "disc",
    paddingLeft: "20px",
    marginTop: "5px",
    fontSize: "0.9rem",
    color: COLOR_PALETTE.botText,
  },
  // --- END NEW STYLES ---
  inputArea: {
    display: "flex",
    alignItems: "center",
    padding: "10px 15px",
    borderTop: `1px solid ${COLOR_PALETTE.borderColor}`,
    background: COLOR_PALETTE.background,
  },
  input: {
    flex: 1,
    padding: "12px",
    borderRadius: "24px",
    border: `1px solid ${COLOR_PALETTE.borderColor}`,
    outline: "none",
    marginRight: "10px",
    fontSize: "0.95rem",
    transition: "border-color 0.2s",
    "&:focus": {
      borderColor: COLOR_PALETTE.primary,
    },
  },
  button: {
    padding: "12px",
    border: "none",
    borderRadius: "50%",
    background: COLOR_PALETTE.primary,
    color: COLOR_PALETTE.userText,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "40px",
    height: "40px",
    transition: "background 0.2s",
    "&:hover": {
      background: "#0F5BB5",
    },
  },
  loading: {
    fontStyle: "italic",
    color: "#888",
    margin: "5px 0 15px 15px",
    fontSize: "0.9rem",
  },
};

const ActionButton = styled.button`
  margin-top: 10px;
  width: 100%;
  padding: 10px;
  background: #d9534f;
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition:
    background 0.2s ease,
    transform 0.15s ease,
    box-shadow 0.15s ease;

  &:hover {
    background: #c9302c;
    transform: translateY(-1px);
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);
  }

  &:active {
    transform: translateY(0);
  }
`;

const Chatbot = () => {
  const [messages, setMessages] = useState([
    {
      role: "bot",
      text: "Hi, I’m the LINQ. How can I help you today?",
      related: [],
    },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [showTicketModal, setShowTicketModal] = useState(false);
  // Store data needed to pass to modal (e.g., the last question)
  const [modalContext, setModalContext] = useState(null);
  const [showRobotIntro, setShowRobotIntro] = useState(true);
  const [showAgentModal, setShowAgentModal] = useState(false);
  const email = useSelector(
    (state) => state.RS_Authentication.user.details.email,
  );
  const selectedAccount = useSelector((state) => state.account.selectedAccount);
  const chatEndRef = useRef(null);

  // Scroll to bottom for new messages
  const scrollToBottom = () => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, loading]);

  const getTopSuggestions = (relatedQuestions = [], limit = 3) => {
    const uniqueQuestions = Array.from(
      new Set(relatedQuestions.map((q) => q.question)),
    );

    return uniqueQuestions.slice(0, limit).map((question) => ({ question }));
  };

  const sendMessage = async (question = input) => {
    if (!question.trim()) return;

    // Save the user's question to context before sending
    setModalContext({ lastQuestion: question });

    const newMessages = [...messages, { role: "user", text: question }];
    setMessages(newMessages);
    setInput("");
    setLoading(true);

    const payload = { question };

    try {
      const response = await axios.post(
        urls.chatbot.askOut,
        payload,
        oracleConfig,
      );

      const data = response?.data;

      if (data?.status === "not_found") {
        setMessages((prev) => [
          ...prev,
          {
            role: "bot",
            text: data.data.action_message,
            related: [],
            notFoundData: data.data,
          },
        ]);
      } else if (response?.data?.status === "error") {
        setMessages((prev) => [
          ...prev,
          {
            role: "bot",
            text: "⚠️ I couldn’t understand that. Could you rephrase or provide a valid question?",
            related: [],
          },
        ]);
      } else {
        // setMessages((prev) => [
        //   ...prev,
        //   {
        //     role: "bot",
        //     text: data?.data?.answer
        //       ? data.data.answer
        //       : "Sorry, I couldn’t find an answer. Try rephrasing your question.",
        //     related: data?.data?.related_questions || [],
        //   },
        // ]);

        const confidenceScore = data?.data?.confidence_score ?? 0;
        console.log({ confidenceScore });
        const relatedQuestions = data?.data?.related_questions || [];

        // ❌ Low-confidence match → treat as NOT FOUND
        if (confidenceScore < 60) {
          const topSuggestions = getTopSuggestions(relatedQuestions);

          setMessages((prev) => [
            ...prev,
            {
              role: "bot",
              text: "We couldn’t find an answer to your question. Please try one of the suggestions below or create a support ticket.",
              related: topSuggestions,
              notFoundData: {
                // suggestions: topSuggestions.map((q) => q.question),
                action: "Create Support Ticket",
                trigger: "confidence_score",
              },
            },
          ]);
        } else {
          // ✅ High-confidence match → show answer
          setMessages((prev) => [
            ...prev,
            {
              role: "bot",
              text: data.data.answer,
              related: getTopSuggestions(relatedQuestions),
            },
          ]);
        }
      }
    } catch (error) {
      setMessages((prev) => [
        ...prev,
        {
          role: "bot",
          text: "⚠️ Error connecting to server. Please try again later.",
          related: [],
        },
      ]);
    }

    setLoading(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") sendMessage();
  };

  // Function to open the modal
  const handleActionClick = () => {
    setShowTicketModal(true);
  };

  // Callback function executed when the ChatbotTicketModal successfully submits the ticket
  const onTicketSuccess = (ticketId) => {
    // 1. Close the modal
    setShowTicketModal(false);

    // 2. Add the success confirmation message to the chat stream
    setMessages((prev) => [
      ...prev,
      {
        role: "bot",
        // The text is a JSX element which will be rendered directly
        text: (
          <>
            <strong
              style={{
                display: "block",
                fontSize: "1.1rem",
                marginBottom: "5px",
              }}
            >
              <CheckCircleFill style={{ marginRight: "5px" }} /> Ticket Created
              Successfully!
            </strong>
            We've logged a support ticket for your issue. A specialist will
            review your question shortly.
            <br />
            <br />
            Your ticket reference number is{" "}
            <strong style={{ color: "#155724" }}>#{ticketId}</strong>. You will
            receive an email confirmation.
          </>
        ),
        isTicketSuccess: true, // Flag to apply the custom green styling
        related: [],
      },
    ]);

    // 3. Clear modal context (optional)
    setModalContext(null);
  };

  // Helper function to render a message
  const renderMessage = (msg, idx, messageRef) => {
    const isUser = msg.role === "user";

    // Determine message style based on role and success flag
    let messageStyle;
    if (isUser) {
      messageStyle = styles.userMessage;
    } else if (msg.isTicketSuccess) {
      messageStyle = styles.ticketSuccessMessage;
    } else {
      messageStyle = styles.botMessage;
    }

    const handleRelatedClick = (question) => {
      if (messageRef.current) {
        messageRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
      sendMessage(question);
    };

    return (
      <div
        key={idx}
        style={{
          ...styles.messageRow,
          justifyContent: isUser ? "flex-end" : "flex-start",
          marginLeft: isUser ? "auto" : "unset",
          marginRight: isUser ? "unset" : "auto",
        }}
        ref={messageRef}
      >
        {!isUser && (
          <div style={styles.avatar}>
            <Robot />
          </div>
        )}
        <div style={{ display: "flex", flexDirection: "column" }}>
          <div style={{ ...styles.message, ...messageStyle }}>
            <pre
              style={{
                margin: 0,
                whiteSpace: "pre-wrap",
                display: "inline-block",
              }}
            >
              {/* Conditional rendering for text: use the element if it's JSX (ticket success), otherwise use the string/fallback */}
              {typeof msg.text === "string" ? msg.text : <>{msg.text}</>}
            </pre>
          </div>

          {/* --- NOT FOUND SUGGESTIONS AND ACTION BUTTON --- */}
          {/* Ensure this section is NOT shown if a ticket success message is displayed */}
          {!isUser && msg.notFoundData && !msg.isTicketSuccess && (
            <div style={styles.notFoundContainer}>
              {msg?.related &&
                msg.related.length > 0 &&
                msg?.notFoundData?.trigger === "confidence_score" && (
                  <div style={styles.relatedContainer}>
                    <strong style={styles.relatedTitle}>
                      Suggested Questions
                    </strong>
                    <div style={styles.relatedList}>
                      {msg.related.map((r, rIdx) => (
                        <button
                          key={r.question_id || rIdx}
                          style={styles.relatedButton}
                          onClick={() => handleRelatedClick(r.question)}
                        >
                          {r.question}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              <p
                style={{
                  margin: "0 0 10px 0",
                  fontWeight: "600",
                  color: "#B56500",
                }}
              >
                🤔 Need more help?
              </p>

              {/* Suggestions List */}
              <ul style={styles.suggestionList}>
                {msg?.notFoundData?.suggestions?.map((s, sIdx) => (
                  <li key={sIdx}>{s}</li>
                ))}
              </ul>

              {/* Action Button */}
              <ActionButton
                // style={styles.actionButton}
                onClick={handleActionClick} // Opens the modal
                disabled={loading}
              >
                <TicketFill style={{ marginRight: "8px" }} />
                {msg.notFoundData.action}
              </ActionButton>

              {/* CXONE ESCALATION BUTTON */}
              {msg.notFoundData && (
                <ActionButton
                  // style={styles.actionButton}
                  onClick={() =>
                    handleLiveAgentEscalation(
                      messages,
                      email,
                      selectedAccount?.label,
                    )
                  }
                >
                  <ChatDotsFill style={{ marginRight: "8px" }} />
                  Chat with Live Agent
                </ActionButton>
              )}
            </div>
          )}
          {/* --- END NOT FOUND SECTION --- */}

          {/* Only show related questions if an answer was found (no notFoundData and no isTicketSuccess) */}
          {!isUser &&
            msg.related &&
            msg.related.length > 0 &&
            !msg.notFoundData &&
            !msg.isTicketSuccess && (
              <div style={styles.relatedContainer}>
                <strong style={styles.relatedTitle}>Suggested Questions</strong>
                <div style={styles.relatedList}>
                  {msg.related.map((r, rIdx) => (
                    <button
                      key={r.question_id || rIdx}
                      style={styles.relatedButton}
                      onClick={() => handleRelatedClick(r.question)}
                    >
                      {r.question}
                    </button>
                  ))}
                </div>
              </div>
            )}
        </div>
        {isUser && (
          <div style={{ ...styles.avatar, color: "#999" }}>
            <PersonCircle />
          </div>
        )}
      </div>
    );
  };

  // function submitToAgentInfo(data) {
  //   handleLiveAgentEscalation(messages, data);
  // }

  return (
    <div style={styles.container}>
      {/* PROFESSIONAL HEADER */}
      {/* {showRobotIntro && (
        <RobotWaveOverlay onFinish={() => setShowRobotIntro(false)} />
      )} */}
      {/* <RobotWaveAnimation /> */}
      <div style={styles.header}>
        <span style={styles.headerIcon}>
          <Robot />
        </span>
        LINQ
      </div>
      {/* END PROFESSIONAL HEADER */}

      <div style={styles.chatWindow}>
        {messages.map((msg, idx) => {
          const messageRef = createRef();
          return renderMessage(msg, idx, messageRef);
        })}
        {loading && <div style={styles.loading}>Assistant is typing...</div>}
        <div ref={chatEndRef} />
      </div>

      <div style={styles.inputArea}>
        <input
          type="text"
          style={styles.input}
          placeholder="Ask a question..."
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyPress}
          disabled={loading || showTicketModal} // Disable input when loading or modal is open
        />
        <button
          style={styles.button}
          onClick={() => sendMessage()}
          disabled={!input.trim() || loading || showTicketModal}
        >
          <SendFill />
        </button>
      </div>

      {/* --- MODAL RENDERED OUTSIDE THE CHAT STREAM --- */}
      {/* Pass the necessary props to your modal component */}
      <ChatbotTicketModal
        show={showTicketModal}
        setShow={setShowTicketModal}
        onTicketSuccess={onTicketSuccess} // Pass the success callback
        // The modal should use this to pre-fill the issue description
        initialQuestion={modalContext?.lastQuestion}
      />
      {/* <LiveAgentModal
        show={showAgentModal}
        setShow={setShowAgentModal}
        onSubmitFunc={submitToAgentInfo}
      /> */}
      {/* --- END MODAL --- */}
    </div>
  );
};

export default Chatbot;

// const RobotWaveOverlay = ({ onFinish }) => {
//   React.useEffect(() => {
//     const timer = setTimeout(() => {
//       onFinish();
//     }, 1500);
//     return () => clearTimeout(timer);
//   }, [onFinish]);

//   return (
//     <div style={overlayStyles.container}>
//       <img src={robotWave} alt="Waving robot" style={overlayStyles.image} />
//       <p style={overlayStyles.text}>Hi there! I’m LINQ 🤖</p>
//     </div>
//   );
// };

const overlayStyles = {
  container: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    background: "rgba(255, 255, 255, 0.92)",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1000,
    animation: "fadeIn 0.5s ease",
  },
  image: {
    width: "180px",
    height: "auto",
  },
  text: {
    marginTop: "15px",
    fontSize: "1.1rem",
    color: "#333",
    fontWeight: "500",
  },
};
